import { useAuthStore } from "../store";
import onBoardingApi from "./onBoardingApi";

export type EventLevel = "INFO" | "ERROR";

export interface UserNotification {
  id: string;
  userId: string;
  eventType: string;
  eventLevel: EventLevel;
  eventData: any;
  message: string;
  expiry: string;
  isRead: boolean;
  timestamp: string;
}

const BASE_URL =
  process.env.REACT_APP_ONBOARDING_URL ||
  "https://onboarding-api.hissa.com/v1/";

export function getSseSubscribeUrl(token: string): string {
  const base = BASE_URL.endsWith("/") ? BASE_URL.slice(0, -1) : BASE_URL;
  return `${base}/sse/subscribe?token=${encodeURIComponent(token)}`;
}

// Channels we subscribe to on the SSE EventSource. The server's actual event
// name isn't pinned (it may emit "DOWNLOAD" directly, or a broader channel
// with the discriminator in payload.eventType). Subscribing to all known
// names is belt-and-braces; the actual filtering happens via
// payload.eventType in each consumer.
export const NOTIFICATION_EVENT_TYPES = [
  "notification",
  "data-pipeline",
  "task-complete",
  "alert",
  "DOWNLOAD",
  "CAPTABLE",
  "FINANCIAL",
  "MCA_ORDER",
] as const;

export type NotificationEventType = (typeof NOTIFICATION_EVENT_TYPES)[number];

export interface SubscribeOptions {
  onMessage: (notification: UserNotification) => void;
  onError?: (err: Event) => void;
  onOpen?: () => void;
  eventTypes?: readonly string[];
}

export interface SubscriptionHandle {
  close: () => void;
}

function normalizeNotification(
  raw: any,
  fallbackEventType: string,
): UserNotification {
  return {
    id: raw?.id ?? `${Date.now()}-${Math.random()}`,
    userId: raw?.userId ?? "",
    eventType: raw?.eventType ?? fallbackEventType,
    eventLevel: (raw?.eventLevel as EventLevel) ?? "INFO",
    eventData: raw?.eventData ?? raw?.data ?? {},
    message: raw?.message ?? "",
    expiry: raw?.expiry ?? "",
    isRead: raw?.isRead ?? false,
    timestamp: raw?.timestamp ?? new Date().toISOString(),
  };
}

function parseEvent(
  event: MessageEvent,
  fallbackEventType: string,
): UserNotification {
  try {
    const parsed =
      typeof event.data === "string" ? JSON.parse(event.data) : event.data;
    return normalizeNotification(parsed, fallbackEventType);
  } catch {
    return normalizeNotification(
      { message: String(event.data ?? "") },
      fallbackEventType,
    );
  }
}

interface InternalListener {
  id: number;
  onMessage: (notification: UserNotification) => void;
  onOpen?: () => void;
  onError?: (err: Event) => void;
  eventTypes: readonly string[];
}

let sharedSource: EventSource | null = null;
let sharedToken: string | null = null;
let listeners: InternalListener[] = [];
let attachedEventTypes = new Set<string>();
let nextListenerId = 1;

function teardownSource() {
  if (sharedSource) {
    sharedSource.close();
    sharedSource = null;
  }
  sharedToken = null;
  attachedEventTypes = new Set<string>();
}

function attachEventType(type: string) {
  if (!sharedSource || attachedEventTypes.has(type)) return;
  attachedEventTypes.add(type);
  sharedSource.addEventListener(type, (event) => {
    const parsed = parseEvent(event as MessageEvent, type);
    listeners.forEach((l) => {
      if (l.eventTypes.includes(type)) l.onMessage(parsed);
    });
  });
}

function ensureSource(token: string): EventSource {
  if (sharedSource && sharedToken === token) return sharedSource;
  teardownSource();
  sharedSource = new EventSource(getSseSubscribeUrl(token));
  sharedToken = token;
  sharedSource.onopen = () => listeners.forEach((l) => l.onOpen?.());
  sharedSource.onerror = (err) => listeners.forEach((l) => l.onError?.(err));
  return sharedSource;
}

export function subscribeToNotifications(
  options: SubscribeOptions,
): SubscriptionHandle | null {
  const token =
    localStorage.getItem("accesstoken") || useAuthStore.getState().accessToken;
  if (!token) return null;

  const source = ensureSource(token);
  const eventTypes = options.eventTypes ?? NOTIFICATION_EVENT_TYPES;
  eventTypes.forEach(attachEventType);

  const listener: InternalListener = {
    id: nextListenerId++,
    onMessage: options.onMessage,
    onOpen: options.onOpen,
    onError: options.onError,
    eventTypes,
  };
  listeners.push(listener);

  if (source.readyState === EventSource.OPEN) {
    queueMicrotask(() => options.onOpen?.());
  }

  return {
    close: () => {
      listeners = listeners.filter((l) => l.id !== listener.id);
      if (listeners.length === 0) teardownSource();
    },
  };
}

export interface ListNotificationsParams {
  limit?: number;
  offset?: number;
  eventType?: string;
  eventLevel?: EventLevel;
  unreadOnly?: boolean;
}

export interface ListNotificationsResponse {
  items: UserNotification[];
  total: number;
  unreadCount: number;
  limit: number;
  offset: number;
}

export async function listNotifications(
  params: ListNotificationsParams = {},
): Promise<ListNotificationsResponse> {
  return onBoardingApi
    .get(`sse/notifications`, { params })
    .then((res) => res.data.data);
}

export type MarkReadBody = { ids: string[] } | { all: true };

export async function markNotificationsRead(
  body: MarkReadBody,
): Promise<{ updated: number }> {
  return onBoardingApi
    .post(`sse/notifications/read`, body)
    .then((res) => res.data.data);
}
